import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import ArticleBody, { formatPostDate, postUrl } from "@/components/ArticleBody";
import { SiteButton } from "@/components/brand/SiteButton";
import { Container, Section, SectionHeading } from "@/components/layout/Section";
import { api } from "@/lib/api";
import { SITE } from "@/config/site";
import { ROUTE_SEO } from "@/config/seoData";

const reveal = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Resources = () => {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => setError(true));
  }, []);

  return (
    <>
      <Seo {...ROUTE_SEO.resources} />
      <Section testid="resources-page" className="pt-32 sm:pt-40">
        <Container className="space-y-14">
          <SectionHeading
            eyebrow="Resources"
            title="Honest notes on building and growing"
            description="Practical writing on software delivery, AI adoption and digital growth — no listicles, no filler."
            testid="resources-heading"
          />
          {error && <p className="text-sm text-red-400" data-testid="resources-error">Articles could not be loaded right now.</p>}
          {posts && posts.length === 0 && (
            <p className="text-sm text-brand-mist" data-testid="resources-empty">New articles are on the way.</p>
          )}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2" data-testid="resources-grid">
            {(posts || []).map((p, i) => (
              <motion.div key={p.slug} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} custom={i}>
                <Link
                  to={`/resources/${p.slug}`}
                  data-testid={`post-card-${p.slug}`}
                  className="group flex h-full flex-col justify-between gap-8 rounded-xl border border-brand-mist/12 bg-brand-charcoal p-8 transition-colors duration-200 hover:border-brand-emerald/35"
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-xs text-brand-mist">
                      <span className="rounded-full border border-brand-emerald/30 bg-brand-emerald/10 px-3 py-1 font-medium text-brand-emerald">{p.category}</span>
                      <span>{formatPostDate(p.publish_at || p.created_at)}</span>
                    </div>
                    <h2 className="font-display text-xl font-semibold leading-snug text-brand-snow sm:text-2xl">{p.title}</h2>
                    <p className="text-sm leading-relaxed text-brand-mist">{p.excerpt}</p>
                  </div>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-emerald">
                    Read article
                    <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
};

export const ArticlePage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api
      .get(`/posts/${slug}`)
      .then((r) => {
        setPost(r.data);
        setState("ok");
      })
      .catch(() => setState("error"));
  }, [slug]);

  if (state === "loading") {
    return (
      <Section className="pt-40" testid="article-loading">
        <Container><p className="text-sm text-brand-mist">Loading article…</p></Container>
      </Section>
    );
  }

  if (state === "error") {
    return (
      <>
        <Seo {...ROUTE_SEO.articleNotFound} />
        <Section className="flex min-h-[60vh] items-center pt-32" testid="article-not-found">
          <Container className="max-w-xl space-y-5">
            <h1 className="font-display text-3xl font-bold text-brand-snow">Article not found</h1>
            <SiteButton to="/resources" variant="outline" testid="article-back-notfound">Back to resources</SiteButton>
          </Container>
        </Section>
      </>
    );
  }

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.seo.description,
      datePublished: post.publish_at || post.created_at,
      author: { "@type": "Organization", name: post.author || SITE.name },
      publisher: { "@type": "Organization", name: SITE.name, url: SITE.domain },
      mainEntityOfPage: postUrl(post.slug),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE.domain },
        { "@type": "ListItem", position: 2, name: "Resources", item: `${SITE.domain}/resources` },
        { "@type": "ListItem", position: 3, name: post.title, item: postUrl(post.slug) },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={post.seo.title}
        description={post.seo.description}
        canonicalPath={`/resources/${post.slug}`}
        ogImage={`${SITE.domain}/og/post/${post.slug}.png`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <article data-testid="article-page">
        <Section className="pt-32 sm:pt-40 pb-10">
          <Container className="max-w-3xl space-y-8">
            <Link to="/resources" data-testid="article-back" className="inline-flex items-center gap-2 text-sm font-medium text-brand-mist transition-colors hover:text-brand-emerald">
              <ArrowLeft size={15} /> All articles
            </Link>
            <div className="space-y-5">
              <div className="flex items-center gap-3 text-xs text-brand-mist">
                <span className="rounded-full border border-brand-emerald/30 bg-brand-emerald/10 px-3 py-1 font-medium text-brand-emerald">{post.category}</span>
                <span>{formatPostDate(post.publish_at || post.created_at)}</span>
                <span aria-hidden="true">·</span>
                <span>{post.author}</span>
              </div>
              <h1 className="font-display text-3xl font-bold leading-[1.12] tracking-tight text-brand-snow sm:text-4xl lg:text-5xl">
                {post.title}
              </h1>
              <p className="text-base leading-relaxed text-brand-mist sm:text-lg">{post.excerpt}</p>
            </div>
          </Container>
        </Section>
        <Section className="pt-0" testid="article-content-section">
          <Container className="max-w-3xl">
            <ArticleBody content={post.content} />
          </Container>
        </Section>
        <Section className="border-t border-brand-mist/10" testid="article-cta">
          <Container className="max-w-3xl">
            <div className="flex flex-col gap-6 rounded-2xl border border-brand-emerald/20 bg-brand-charcoal p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
              <div className="space-y-2">
                <h2 className="font-display text-xl font-bold text-brand-snow">Building something?</h2>
                <p className="text-sm text-brand-mist">A straight conversation about scope, approach and fit.</p>
              </div>
              <SiteButton to="/contact?source=resources" testid="article-cta-contact">
                Book a free discovery call <ArrowRight size={15} />
              </SiteButton>
            </div>
          </Container>
        </Section>
      </article>
    </>
  );
};
